/**
 * The page arithmetic of a paged listing (category, brand, search), shared by
 * the pager in templates/store/catalog/catalog.vue and the page navigation in
 * pages/store/category/category.vue so the two can never disagree.
 *
 * Everything is derived from the page position and the server's total. The
 * number of products a page renders is NOT a measure of where it ends: a
 * product whose own data is broken is left out of its page
 * (utils/graphqlPartialData), so a last page of 2 can render 1. Counting
 * rendered products showed View Next on that last page, and the listing then
 * refused the click as past the end: a silent no-op.
 */

export type CatalogPageWindowInput = {
  currentPage: number;
  pageSize: number;
  totalElements: number;
  /** Products the page renders (after any were left out). */
  itemsOnPage: number;
};

export type CatalogPageWindow = {
  totalPages: number;
  hasNextPage: boolean;
  /** First listing position of this page, or 0 when it renders nothing. */
  visibleStart: number;
  /** Last listing position this page covers (never past the total). */
  visibleEnd: number;
};

const wholeNumber = (value: unknown, minimum: number) =>
  Math.max(minimum, Math.floor(Number(value) || 0));

/** Pages of `pageSize` needed for `totalElements`; at least 1. */
export const catalogTotalPages = (totalElements: number, pageSize: number) =>
  Math.max(
    1,
    Math.ceil(wholeNumber(totalElements, 0) / wholeNumber(pageSize, 1))
  );

export const catalogPageWindow = ({
  currentPage,
  pageSize,
  totalElements,
  itemsOnPage,
}: CatalogPageWindowInput): CatalogPageWindow => {
  const size = wholeNumber(pageSize, 1);
  const page = wholeNumber(currentPage, 1);
  const total = wholeNumber(totalElements, 0);
  const items = wholeNumber(itemsOnPage, 0);
  const totalPages = catalogTotalPages(total, size);
  const pageStart = (page - 1) * size;

  return {
    totalPages,
    hasNextPage: page < totalPages,
    visibleStart: items > 0 ? pageStart + 1 : 0,
    visibleEnd: Math.min(total, items > 0 ? page * size : pageStart),
  };
};

/**
 * True when a page had products but every one of them was left out for
 * broken data. That is a failed answer, never "no products".
 */
export const pageLostEveryItem = (itemsOnPage: number, droppedItems: number) =>
  wholeNumber(itemsOnPage, 0) === 0 && wholeNumber(droppedItems, 0) > 0;

/**
 * View Next got an answer without errors but with no products. When the
 * listing shrank (products sold out or unpublished since the current page
 * loaded) the target page no longer exists: this returns the total to show,
 * never below the end of the page already on screen, so the pager hides View
 * Next. Otherwise (going back, an answer whose own total still covers the
 * target page, products left out for broken data) it returns null and the
 * caller reports a failure.
 */
export const shrunkListingTotal = ({
  targetPage,
  currentPage,
  pageSize,
  responseTotal,
  droppedItems = 0,
  shownThroughCurrentPage,
}: {
  targetPage: number;
  currentPage: number;
  pageSize: number;
  responseTotal: unknown;
  droppedItems?: number;
  shownThroughCurrentPage: number;
}): number | null => {
  if (wholeNumber(droppedItems, 0) > 0) return null;
  if (wholeNumber(targetPage, 1) <= wholeNumber(currentPage, 1)) return null;
  const total = wholeNumber(responseTotal, 0);
  if (total > (wholeNumber(targetPage, 1) - 1) * wholeNumber(pageSize, 1)) {
    return null;
  }
  return Math.max(total, wholeNumber(shownThroughCurrentPage, 0));
};
